import { create } from 'zustand';

import { OpenDrawer, StackEntry } from './drawer-manager.model';
import { DrawerId } from './drawer-registry';

const CLOSE_ANIMATION_DURATION_MS = 300;

// Timers live outside the store — they are side effects, not state.
const timers = new Map<DrawerId, ReturnType<typeof setTimeout>>();

interface DrawerStore {
  stack: StackEntry[];
  open: OpenDrawer;
  close: (id: DrawerId) => void;
  closeAll: () => void;
  isOpen: (id: DrawerId) => boolean;
}

const clearExistingTimer = (id: DrawerId) => {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
};

const scheduleRemoval = (
  set: (fn: (s: DrawerStore) => Partial<DrawerStore>) => void,
  id: DrawerId,
) => {
  timers.set(
    id,
    setTimeout(() => {
      set((s: DrawerStore) => ({
        stack: s.stack.filter((e: StackEntry) => e.id !== id),
      }));
      timers.delete(id);
    }, CLOSE_ANIMATION_DURATION_MS),
  );
};

const useDrawerStore = create<DrawerStore>((set, get) => ({
  stack: [],

  open: (id, ...args) => {
    const props = (args[0] ?? {}) as Record<string, unknown>;
    clearExistingTimer(id);
    set((s) => {
      const exists = s.stack.some((e) => e.id === id);
      if (exists) {
        return {
          stack: s.stack.map((e) =>
            e.id === id ? { ...e, phase: 'open', props } : e,
          ),
        };
      }
      return {
        stack: [...s.stack, { id, props, phase: 'open' }],
      };
    });
  },

  close: (id) => {
    set((s) => ({
      stack: s.stack.map((e) => (e.id === id ? { ...e, phase: 'closing' } : e)),
    }));
    clearExistingTimer(id);
    scheduleRemoval(set, id);
  },

  closeAll: () => {
    const openIds = get()
      .stack.filter((e) => e.phase === 'open')
      .map((e) => e.id);
    set((s) => ({
      stack: s.stack.map((e) => ({ ...e, phase: 'closing' })),
    }));
    openIds.forEach((id) => {
      clearExistingTimer(id);
      scheduleRemoval(set, id);
    });
  },

  isOpen: (id) => get().stack.some((e) => e.id === id && e.phase === 'open'),
}));

export { useDrawerStore };
